import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_ORGANIZACAO_E_CONTA } from "@/lib/ajuda/dialogos/organizacao-e-conta";
import { cn } from "@/lib/cn";

/**
 * Editar os procedimentos de uma especialidade: marcado = a clínica faz. Desmarcar um que ela já tem
 * desativa (não apaga — consultas e planos antigos continuam apontando pra ele). Ao abrir logo depois
 * de adicionar a especialidade (`novos`), os que faltam já vêm marcados.
 */
export function ProcedimentosDaEspecialidadeDialog({
  especialidade,
  novos,
  onClose,
}: {
  especialidade: { id: string; label: string } | null;
  novos?: boolean;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const lista = trpc.clinicaOnboarding.daEspecialidade.useQuery({ specialtyId: especialidade?.id ?? "" }, { enabled: !!especialidade });
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (lista.data) setMarcados(new Set(lista.data.filter((p) => p.ativo || novos).map((p) => p.id)));
  }, [lista.data, novos]);

  const ajustar = trpc.clinicaOnboarding.ajustar.useMutation({
    onSuccess: ({ adicionados, desativados }) => {
      utils.clinicaOnboarding.invalidate();
      utils.service.invalidate();
      const partes = [adicionados && `${adicionados} adicionado(s)`, desativados && `${desativados} desativado(s)`].filter(Boolean);
      toast.success(partes.length ? `Procedimentos: ${partes.join(", ")}.` : "Nada mudou.");
      onClose();
    },
    onError: (e) => toast.error(e.message || "Não foi possível salvar."),
  });

  const itens = lista.data ?? [];
  // Agrupados pela especialidade principal de cada procedimento.
  const categorias = [...new Set(itens.map((p) => p.grupo))];
  const alternar = (id: string) =>
    setMarcados((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  const salvar = () =>
    ajustar.mutate({
      adicionar: itens.filter((p) => marcados.has(p.id) && !p.servicoId).map((p) => p.id),
      reativar: itens.filter((p) => marcados.has(p.id) && p.servicoId && !p.ativo).map((p) => p.servicoId!),
      desativar: itens.filter((p) => !marcados.has(p.id) && p.servicoId && p.ativo).map((p) => p.servicoId!),
    });

  return (
    <Dialog open={!!especialidade} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 border border-zinc-200 bg-white p-0 shadow-xl sm:max-w-lg dark:border-border dark:bg-card">
        <DialogHeader className="border-b border-zinc-100 px-5 py-4 text-left dark:border-border">
          <DialogTitle ajuda={AJUDA_ORGANIZACAO_E_CONTA.procedimentosDaEspecialidade}>Procedimentos de {especialidade?.label}</DialogTitle>
          <DialogDescription>Marque o que a clínica faz. Desmarcar desativa o procedimento, sem apagar o histórico.</DialogDescription>
        </DialogHeader>
        <div className="min-h-[160px] flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {lista.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
            </div>
          ) : itens.length === 0 ? (
            <p className="py-10 text-center text-sm text-zinc-500 dark:text-muted-foreground">A base ainda não tem procedimentos desta especialidade.</p>
          ) : (
            categorias.map((c) => (
              <section key={c}>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">{c}</p>
                <div className="divide-y divide-zinc-100 overflow-hidden rounded-lg border border-zinc-200 dark:divide-border dark:border-border">
                  {itens
                    .filter((p) => p.grupo === c)
                    .map((p) => {
                      const marcado = marcados.has(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => alternar(p.id)}
                          aria-pressed={marcado}
                          className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-zinc-50 dark:hover:bg-muted"
                        >
                          <span
                            className={cn(
                              "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                              marcado ? "border-brand-600 bg-brand-600 text-white" : "border-zinc-300 dark:border-border",
                            )}
                          >
                            {marcado && <Check className="h-3 w-3" />}
                          </span>
                          <span className="flex-1 text-zinc-900 dark:text-foreground">{p.nome}</span>
                          {p.servicoId && !p.ativo && <span className="text-[11px] text-zinc-400">Inativo</span>}
                        </button>
                      );
                    })}
                </div>
              </section>
            ))
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-100 px-5 py-3 dark:border-border">
          <button type="button" onClick={onClose} className="h-9 rounded-md px-3 text-sm font-medium text-zinc-600 hover:bg-zinc-50 dark:text-muted-foreground dark:hover:bg-muted">
            Cancelar
          </button>
          <button
            type="button"
            disabled={ajustar.isPending || lista.isLoading}
            onClick={salvar}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {ajustar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Salvar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
